import React, { FC } from "react";
import { useIntl } from "react-intl";
import { useErrorController } from "@natera/platform/lib/hooks";
import { validateZipCode } from "@app/utils";
import { messages } from "./messages";
import { Form } from "@natera/form";
import { Address } from "@app/provider/profile";
import {
  StreetAddressField,
  ApartmentUnitField,
  CityField,
  StateField,
  ZipCodeField,
} from "@app/components/forms/addressForm/formElements";

interface Props {
  address?: Address;
  onSubmit: (address: Partial<Address>) => void;
  actions: React.ReactNode;
}

const AddressForm: FC<Props> = ({ address, onSubmit, actions }) => {
  const intl = useIntl();

  const { clearErrors, getValidationError, setValidationError } =
    useErrorController();

  const streetAddress = address?.street1 || "";
  const apartmentUnit = address?.street2 || "";

  React.useEffect(() => {
    const alerts = document.querySelectorAll(
      ".mdc-text-field-helper-text[role='alert']"
    );
    alerts.forEach((el) => el.removeAttribute("role"));
  });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    clearErrors();

    const formData = new FormData(event.currentTarget);

    const streetAddress = formData.get("streetAddress")?.toString();
    const apartmentUnit = formData.get("apartmentUnit")?.toString();
    const city = formData.get("city")?.toString();
    const state = formData.get("state")?.toString();
    const zipCode = formData.get("zipCode")?.toString();

    const addressForm = [
      { title: "streetAddress", value: streetAddress && streetAddress.trim() },
      { title: "city", value: city && city.trim() },
      { title: "state", value: state },
    ];

    const errors: Record<string, string> = {};

    for (const field of addressForm) {
      if (!field.value) {
        errors[field.title] = intl.formatMessage(messages.addressFormRequired);
      }
    }

    if (!zipCode || !validateZipCode(zipCode)) {
      errors.zipCode = intl.formatMessage(messages.addressFormInvalidZipCode);
    }

    if (Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(([field, message]) => {
        setValidationError(field, message);
      });

      const firstErrorField = document.getElementById(Object.keys(errors)[0]);
      firstErrorField?.focus();
      return;
    }

    const newAddress: Partial<Address> = {
      city,
      state,
      street1: streetAddress,
      street2: apartmentUnit,
      zipCode,
    };
    if (address) {
      newAddress.uid = address.uid;
    }
    onSubmit(newAddress);
  };

  return (
    <Form
      noValidate
      className="info-card-form"
      data-testid="addressForm"
      onSubmit={handleSubmit}
      buttons={actions}
    >
      <StreetAddressField
        getValidationError={getValidationError}
        streetAddress={streetAddress}
      />
      <ApartmentUnitField
        getValidationError={getValidationError}
        apartmentUnit={apartmentUnit}
      />
      <CityField
        defaultValue={address?.city}
        getValidationError={getValidationError}
      />
      <StateField
        defaultValue={address?.state}
        getValidationError={getValidationError}
      />
      <ZipCodeField
        defaultValue={address?.zipCode}
        getValidationError={getValidationError}
      />
    </Form>
  );
};

export default AddressForm;
